/**
 * NotificationPreferencesForm component - Complete form for managing notification settings
 * 
 * Allows users to configure:
 * - Email notification enable/disable
 * - Notification frequency (daily/weekly)
 * - Preferred send time (hour and minute)
 * - Timezone for scheduling
 */
import React, { useState, useEffect, useId } from 'react';
import { Bell, Clock, Globe, Calendar } from 'lucide-react';
import Button from '../Button';
import { TimePickerInput } from './TimePickerInput';
import { TIMEZONE_OPTIONS, getDefaultTimezoneOption } from '../../utils/timezones';
import type { NotificationPreference, NotificationPreferenceUpdate } from '../../types/notification';

interface NotificationPreferencesFormProps {
  preferences: NotificationPreference | null;
  onSubmit: (data: NotificationPreferenceUpdate) => Promise<void>;
  loading?: boolean;
}

export const NotificationPreferencesForm: React.FC<NotificationPreferencesFormProps> = ({
  preferences,
  onSubmit,
  loading = false,
}) => {
  const [emailEnabled, setEmailEnabled] = useState<boolean>(true);
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>('daily');
  const [hour, setHour] = useState<number>(9);
  const [minute, setMinute] = useState<number>(0);
  const [timezone, setTimezone] = useState<string>('UTC');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Three of the controls below are labelled by text that is not a <label> for anything: the
  // toggle is an unnamed checkbox next to a heading, and "Notification Frequency" heads a pair
  // of buttons rather than a single field. Assistive technology announced an unlabelled switch
  // and two loose buttons, so the text is wired up explicitly by id.
  const emailToggleLabelId = useId();
  const frequencyLabelId = useId();
  const timezoneId = useId();

  // Initialize form with preferences or defaults
  useEffect(() => {
    if (preferences) {
      setEmailEnabled(preferences.email_notifications_enabled);
      setFrequency(preferences.notification_frequency === 'weekly' ? 'weekly' : 'daily');
      setHour(preferences.preferred_hour);
      setMinute(preferences.preferred_minute);
      setTimezone(preferences.timezone);
    } else {
      // Set default timezone to user's browser timezone
      const defaultTz = getDefaultTimezoneOption();
      setTimezone(defaultTz.value);
    }
  }, [preferences]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    // The select can hold a value that is not one of its options — stored preferences with an
    // empty timezone leave it blank, and submitting sent `timezone: ''` to the API as though the
    // user had chosen it. Nothing else on this form needs checking: every other field is a
    // bounded choice.
    if (!timezone) {
      setError('Choose a timezone before saving.');
      return;
    }

    setSubmitting(true);

    try {
      const data: NotificationPreferenceUpdate = {
        email_notifications_enabled: emailEnabled,
        notification_frequency: frequency,
        preferred_hour: hour,
        preferred_minute: minute,
        timezone,
      };

      await onSubmit(data);
      setSuccessMessage('Notification preferences saved successfully!');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save preferences';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTimeChange = (newHour: number, newMinute: number) => {
    setHour(newHour);
    setMinute(newMinute);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="p-4 bg-success/10 dark:bg-success/20 border border-success/30 rounded-lg text-success">
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-error/10 dark:bg-error/20 border border-error/30 rounded-lg text-error">
          {error}
        </div>
      )}

      {/* Email Notifications Toggle */}
      <div className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <Bell className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <h3
                id={emailToggleLabelId}
                className="text-sm font-semibold text-gray-900 dark:text-white"
              >
                Email Notifications
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Receive email alerts for upcoming and overdue tasks
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                role="switch"
                aria-labelledby={emailToggleLabelId}
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Notification Frequency */}
      <div className="space-y-2" role="group" aria-labelledby={frequencyLabelId}>
        <span
          id={frequencyLabelId}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          <Calendar className="w-4 h-4" aria-hidden="true" />
          Notification Frequency
        </span>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setFrequency('daily')}
            aria-pressed={frequency === 'daily'}
            disabled={!emailEnabled}
            className={`p-4 border-2 rounded-lg text-sm font-medium transition-all ${
              frequency === 'daily'
                ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-primary-900 dark:text-primary-100'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
            } ${
              !emailEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            <div className="font-semibold">Daily</div>
            <div className="text-xs mt-1 opacity-75">Every day at selected time</div>
          </button>
          <button
            type="button"
            onClick={() => setFrequency('weekly')}
            aria-pressed={frequency === 'weekly'}
            disabled={!emailEnabled}
            className={`p-4 border-2 rounded-lg text-sm font-medium transition-all ${
              frequency === 'weekly'
                ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-primary-900 dark:text-primary-100'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
            } ${
              !emailEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            <div className="font-semibold">Weekly</div>
            <div className="text-xs mt-1 opacity-75">Once per week (Monday)</div>
          </button>
        </div>
      </div>

      {/* Preferred Time */}
      <div className="opacity-100">
        <TimePickerInput
          hour={hour}
          minute={minute}
          onChange={handleTimeChange}
          disabled={!emailEnabled}
          label="Preferred Notification Time"
        />
      </div>

      {/* Timezone Selection */}
      <div className="space-y-2">
        <label
          htmlFor={timezoneId}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          <Globe className="w-4 h-4" aria-hidden="true" />
          Timezone
        </label>
        <select
          id={timezoneId}
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          disabled={!emailEnabled}
          className="w-full p-2.5 text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg transition-all focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:bg-gray-100 disabled:dark:bg-gray-900 disabled:cursor-not-allowed"
        >
          {TIMEZONE_OPTIONS.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label} ({tz.offset})
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Notifications will be scheduled in your local timezone
        </p>
      </div>

      {/* Submit Button */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="submit"
          variant="primary"
          disabled={loading || submitting || !emailEnabled}
          leftIcon={emailEnabled ? Clock : undefined}
        >
          {submitting ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>

      {/* Info Message */}
      {!emailEnabled && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-900 dark:text-amber-100">
          <strong>Note:</strong> Email notifications are currently disabled. Enable them to receive task reminders.
        </div>
      )}
    </form>
  );
};

export default NotificationPreferencesForm;
